import { signOut } from "@internal/auth";
import { AppShell, NavItem, Sidebar } from "@internal/ui";
import {
  Calendar,
  Home,
  List,
  Settings as SettingsIcon,
  Sparkles,
  User,
} from "lucide-react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { SyncStatus } from "@/components/shared/SyncStatus";
import { Avatar } from "@/components/ui/Avatar";
import { Logo } from "@/components/ui/Logo";
import { useObservable } from "@/hooks/useObservable";
import { useProfile } from "@/hooks/useProfile";
import { parentWeekService } from "@/services/parentWeek.service";

function Brand() {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        lineHeight: 1,
      }}
      data-testid="brand-home"
    >
      <Logo size={26} />
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          fontSize: 19,
          fontWeight: 500,
          letterSpacing: -0.5,
        }}
      >
        Ukepenger
      </span>
    </div>
  );
}

function SidebarContent() {
  const { pathname } = useLocation();
  const { profile } = useProfile();
  const awaitingCloseCount = useObservable(
    parentWeekService.awaitingCloseCount$,
    0,
  );

  const isWeeks =
    pathname === "/parent/weeks" || pathname.startsWith("/parent/weeks/");

  return (
    <Sidebar>
      <Sidebar.Top>
        <NavItem
          to="/parent/dashboard"
          label="Oversikt"
          icon={<Home size={17} />}
          active={pathname === "/parent/dashboard"}
          data-testid="nav-dashboard"
        />
        <NavItem
          to="/parent/weeks"
          label="Uker"
          icon={<Calendar size={17} />}
          active={isWeeks}
          badge={awaitingCloseCount}
          data-testid="nav-weeks"
          badgeTestId="nav-badge-weeks"
        />
      </Sidebar.Top>
      <Sidebar.Bottom>
        <NavItem
          to="/parent/jobs"
          label="Jobber"
          icon={<List size={17} />}
          active={pathname === "/parent/jobs"}
          data-testid="nav-jobs"
        />
        <NavItem
          to="/parent/rewards"
          label="Belønninger"
          icon={<Sparkles size={17} />}
          active={pathname === "/parent/rewards"}
          data-testid="nav-rewards"
        />
        <NavItem
          to="/parent/family"
          label="Familien"
          icon={<User size={17} />}
          active={pathname === "/parent/family"}
          data-testid="nav-family"
        />
        <NavItem
          to="/settings"
          label="Innstillinger"
          icon={<SettingsIcon size={17} />}
          active={pathname === "/settings"}
          data-testid="nav-settings"
        />
      </Sidebar.Bottom>
      {profile ? (
        <Sidebar.User
          avatar={<Avatar person={profile} size={34} />}
          name={profile.data.name}
          role="Forelder"
          onSignOut={() => signOut()}
          data-testid="signout-button"
        />
      ) : null}
    </Sidebar>
  );
}

export function ParentLayout({ children }: { children: ReactNode }) {
  return (
    <AppShell
      brand={<Brand />}
      sidebar={<SidebarContent />}
      status={<SyncStatus />}
    >
      {children}
    </AppShell>
  );
}
